
import { SecretModel } from '@/models/secret-model.type'
import { Component, Vue } from 'vue-property-decorator'
import { AxiosResponse } from 'axios'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import FormDialog from '@/components/FormDialog.vue'
import { Validators } from '@/components/validation/validators'

@Component({
  components: {
    ConfirmationDialog,
    FormDialog
  }
})
export default class Secrets extends Vue {
  headers = [
    { text: 'Secret name', value: 'name', filterable: true },
    { text: 'Content type', value: 'contentType', filterable: true },
    { text: 'Tags (click to copy)', value: 'tags' },
    { text: 'Actions', value: 'action', sortable: false, filterable: false }
  ]
  search: string = ''
  secrets: Array<SecretModel> = []
  vaultName: string = ''
  // snackbar
  snackbar: boolean = false
  snackbarText: string = ''
  // delete confirmation dialog
  deleteDialog: boolean = false
  deleteDialogText: string = ''
  secretToDelete!: SecretModel
  // form dialog
  formDialog: boolean = false
  formDialogTitle: string = 'New secret'
  editedItem: SecretModel = new SecretModel()
  editedItemIndex: number = -1
  passwordFieldType: string = 'password'
  predefinedTags: string[] = ['Url', 'Username', 'IP Address'];
  newTag: string = ''
  creatingNewTag: boolean = false
  isValid: boolean = false

  validators = new Validators()

  nameRules = [
    this.validators.Required('Name is required'),
    this.validators.RegEx(/^[a-zA-Z0-9-]*$/, 'Name can contain only letters, numbers and dashes')
  ]

  private created () {
    this.vaultName = this.$route.params.vault

    this.axios.get('api/store/vaults/' + this.vaultName + '/secrets').then((response: AxiosResponse<Array<any>>) => {
      this.secrets = response.data.map<SecretModel>((val, idx, arr) => {
        let secretModel = new SecretModel()
        secretModel.contentType = val.contentType
        secretModel.tags = val.tags
        secretModel.value = val.value
        secretModel.name = val.identifier.name

        return secretModel
      })
    })
  }

  copyPassword (secretName: string) {
    this.getSecret(secretName).then(response => {
      this.copy(secretName, 'Password', response.data.value)
    })
  }

  copy (secretName: string, key: string, value: string) {
    const el = document.createElement('textarea')
    el.value = value

    let container = null

    if (this.$refs.form && (this.$refs.form as any).$el) {
      container = (this.$refs.form as any).$el
    } else {
      container = document.body
    }

    container.appendChild(el)

    el.select()
    document.execCommand('copy')
    container.removeChild(el)

    this.snackbar = true
    this.snackbarText = secretName + ' - ' + key + ' copied to clipboard!'
  }

  showPassword () {
    this.passwordFieldType = 'text'
  }

  hidePassword () {
    this.passwordFieldType = 'password'
  }

  // GET methods
  private getSecret (secretName: string) : Promise<AxiosResponse<SecretModel>> {
    return this.axios.get<SecretModel>('api/store/vaults/' + this.vaultName + '/secrets/' + secretName)
  }

  // EDIT methods
  editItem (item: SecretModel) {
    if (this.$refs.form) {
      (this.$refs.form as any).resetValidation()
    }

    this.editedItemIndex = this.secrets.indexOf(item)
    this.editedItem = Object.assign({}, item)
    this.editedItem.tags = Object.assign({}, item.tags)

    this.editedItem.isNew = false

    // fetch password
    this.getSecret(item.name).then(response => {
      this.editedItem.value = response.data.value
    })

    this.formDialogTitle = 'Edit secret'
    this.formDialog = true
  }

  newItem () {
    if (this.$refs.form) {
      (this.$refs.form as any).resetValidation()
    }

    this.editedItem = new SecretModel()
    this.editedItem.tags = {}

    this.editedItem.isNew = true

    this.formDialogTitle = 'New secret'
    this.formDialog = true
  }

  filterPredefinedTags () {
    let toReturn = []

    for (let idx in this.predefinedTags) {
      if (this.editedItem.tags) {
        if (!Object.keys(this.editedItem.tags).some(value => value.toLowerCase() === this.predefinedTags[idx].toLowerCase())) {
          toReturn.push(this.predefinedTags[idx])
        }
      }
    }

    return toReturn
  }

  removeTag (key: string) {
    Vue.delete(this.editedItem.tags, key)
  }

  addTag () {
    if (!this.newTag || Object.keys(this.editedItem.tags).some(value => value.toLowerCase() === this.newTag.toLowerCase())) {
      return
    }

    Vue.set(this.editedItem.tags, this.newTag, '')
    this.newTag = ''
  }

  openUrl (url: string) {
    window.open(url, '_blank')
  }

  save () {
    if (!this.isValid) {
      return
    }

    this.axios.post<SecretModel>('api/store/vaults/' + this.vaultName + '/secrets/' + this.editedItem.name, this.editedItem).then(response => {
      let toInsert = Object.assign({}, this.editedItem)
      toInsert.tags = Object.assign({}, this.editedItem.tags)

      if (!this.editedItem.isNew) {
        this.secrets.splice(this.editedItemIndex, 1, toInsert)
      } else {
        this.secrets.splice(0, 0, toInsert)
      }

      // reset control fields
      this.editItemCancelled()
    })
  }

  editItemCancelled () {
    this.formDialog = false
    this.newTag = ''
    this.creatingNewTag = false
    this.editedItemIndex = -1
  }

  // DELETE methods
  deleteSecret (data: SecretModel) {
    this.deleteDialog = true
    this.secretToDelete = data
    this.deleteDialogText = 'Are you certain you wish to delete secret with the name \'' + data.name + '\'?'
  }

  deleteSecretConfirmed () {
    this.axios.delete('api/store/vaults/' + this.vaultName + '/secrets/' + this.secretToDelete.name).then(response => {
      const idx = this.secrets.indexOf(this.secretToDelete)
      this.secrets.splice(idx, 1)

      this.deleteDialog = false
    })
  }

  deleteSecretCancelled () {
    this.deleteDialog = false
  }
}
